import { uiText } from "@/lib/i18n/uiText";
import { useEffect, useState } from "react";
import { Download, Share, PlusSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getInstallEvent, isIos, isStandalone, onInstallChange, promptInstall } from "@/lib/pwa/register";

/** Shared hook: whether "Install Statlign" can be offered on this device. */
export function useInstallAvailability() {
  const [state, setState] = useState<{ canPrompt: boolean; ios: boolean; installed: boolean }>({ canPrompt: false, ios: false, installed: true });
  useEffect(() => {
    const update = () => setState({ canPrompt: !!getInstallEvent(), ios: isIos(), installed: isStandalone() });
    update();
    const off = onInstallChange(update);
    return () => {
      off();
    };
  }, []);
  return { ...state, available: !state.installed && (state.canPrompt || state.ios) };
}

export function IosInstallSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{uiText("Install Statlign on iPhone")}</DialogTitle>
          <DialogDescription>{uiText("Three taps for one-tap access to today's picks.")}</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 text-sm">
          <li className="flex items-center gap-3"><span className="font-semibold">1.</span>  {uiText("Tap")} <Share className="h-4 w-4" aria-label={uiText("Share")} />  {uiText("Share in Safari's toolbar.")}</li>
          <li className="flex items-center gap-3"><span className="font-semibold">2.</span>  {uiText("Choose")} <PlusSquare className="h-4 w-4" aria-hidden />  {uiText("\"Add to Home Screen\".")}</li>
          <li className="flex items-center gap-3"><span className="font-semibold">3.</span>  {uiText("Tap \"Add\" — Statlign opens full screen like an app.")}</li>
        </ol>
      </DialogContent>
    </Dialog>
  );
}

export function InstallAppButton({ className, variant = "outline", label = "Install Statlign" }: { className?: string; variant?: "outline" | "ghost" | "default"; label?: string }) {
  const { available, canPrompt, ios } = useInstallAvailability();
  const [iosOpen, setIosOpen] = useState(false);
  if (!available) return null;
  return (
    <>
      <Button
        type="button"
        size="sm"
        variant={variant}
        className={className}
        onClick={() => (canPrompt ? void promptInstall() : ios ? setIosOpen(true) : undefined)}
      >
        <Download className="h-4 w-4 mr-1.5" aria-hidden /> {label}
      </Button>
      <IosInstallSheet open={iosOpen} onOpenChange={setIosOpen} />
    </>
  );
}
